import React from 'react';
import { Shield, Heart, Sparkles, Users, ArrowRight } from 'lucide-react';

const ColorTest = () => {
  // Color palettes to test
  const palettes = [
    {
      name: 'Actual (Verde Oliva)',
      primary: '#2D3328',
      primaryLight: '#3a4235',
      accent: '#c5a059',
      description: 'El verde actual - puede sentirse frío, militar, poco luminoso',
      issue: 'Demasiado apagado y frío para una escuela terapéutica'
    },
    {
      name: 'Opción A: Tierra Cálida',
      primary: '#5D4E42',
      primaryLight: '#6d5e52',
      accent: '#c5a059',
      description: 'Marrón tierra cálido - evoca raíces, tierra, calidez',
      issue: 'Grounding, acogedor, conecta con "arquitectura interior"'
    },
    {
      name: 'Opción B: Azul Profundo',
      primary: '#1e3a5f',
      primaryLight: '#2a4a73',
      accent: '#c9a227',
      description: 'Azul profundo (como Campus) - serenidad, profundidad, confianza',
      issue: 'Transmite calma y profundidad espiritual'
    },
    {
      name: 'Opción C: Borgona Suave',
      primary: '#5c3d3d',
      primaryLight: '#6d4e4e',
      accent: '#c5a059',
      description: 'Borgona/terracota - calidez, corazón, amor encarnado',
      issue: 'Cálido, íntimo, conecta con el corazón'
    },
    {
      name: 'Opción D: Gris Cálido',
      primary: '#4a4543',
      primaryLight: '#5a5553',
      accent: '#c5a059',
      description: 'Gris piedra con calidez - elegante, neutro, atemporal',
      issue: 'Sofisticado, permite que el dorado brille'
    },
    {
      name: 'Opción E: Teal Profundo',
      primary: '#2d4a4a',
      primaryLight: '#3d5a5a',
      accent: '#c5a059',
      description: 'Verde-azul equilibrado - sanador, fluido, profundo',
      issue: 'Equilibrio entre tierra y cielo, muy terapéutico'
    },
    {
      name: 'Opción F: Berenjena Suave',
      primary: '#4a3f4f',
      primaryLight: '#5a4f5f',
      accent: '#c5a059',
      description: 'Púrpura tierra - espiritualidad encarnada, transformación',
      issue: 'Conecta consciencia elevada con lo terrenal'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-6">
        <h1 className="text-4xl font-heading text-center mb-4">Estudio de Paletas de Color</h1>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Análisis profesional de alternativas al verde actual. Cada opción se muestra en contexto 
          real para evaluar cómo transmite la esencia de la escuela: <strong>terapéutica, luminosa, 
          cálida, espiritual encarnada</strong>.
        </p>

        <div className="space-y-16">
          {palettes.map((palette, index) => (
            <div key={index} className="border-t pt-12 first:border-t-0 first:pt-0">
              {/* Palette header */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 gap-4">
                <div>
                  <h2 className="text-2xl font-heading mb-2">{palette.name}</h2>
                  <p className="text-gray-600">{palette.description}</p>
                  <p className="text-sm mt-1 font-medium" style={{ color: palette.primary }}>
                    → {palette.issue}
                  </p>
                </div>
                <div className="flex gap-3">
                  <div 
                    className="w-16 h-16 rounded-xl shadow-md flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: palette.primary }}
                  >
                    Primary
                  </div>
                  <div 
                    className="w-16 h-16 rounded-xl shadow-md flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: palette.primaryLight }}
                  >
                    Light
                  </div>
                  <div 
                    className="w-16 h-16 rounded-xl shadow-md flex items-center justify-center text-white text-xs"
                    style={{ backgroundColor: palette.accent }}
                  >
                    Accent
                  </div>
                </div>
              </div>

              {/* Mini preview section */}
              <div 
                className="rounded-3xl overflow-hidden shadow-lg"
                style={{ background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)` }}
              >
                <div className="p-8 lg:p-12">
                  {/* Badge */}
                  <div 
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    <Shield className="w-4 h-4" style={{ color: palette.accent }} />
                    <span className="text-sm text-white/90">Formación Especial · En vivo</span>
                  </div>

                  <h3 className="text-3xl lg:text-4xl font-heading text-white mb-2 tracking-tight">
                    LÍMITES
                  </h3>
                  <p className="text-xl text-white/70 font-heading mb-4">
                    La protección original de la consciencia
                  </p>
                  <p className="text-white/60 mb-8 max-w-xl">
                    Un master-curso encarnado para recuperar tu eje sin culpa, sin dureza y sin perder el corazón.
                  </p>

                  {/* Pillar cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                    {[
                      { icon: Heart, text: 'Sistema nervioso' },
                      { icon: Sparkles, text: 'Campo energético' },
                      { icon: Users, text: 'Vínculo y comunicación' },
                      { icon: Shield, text: 'Sin bypass espiritual' }
                    ].map((item, i) => (
                      <div 
                        key={i}
                        className="rounded-xl p-4"
                        style={{ backgroundColor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1 }}
                      >
                        <item.icon className="w-5 h-5 mb-2" style={{ color: palette.accent }} strokeWidth={1.5} />
                        <p className="text-sm text-white/80">{item.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-xl lg:text-2xl font-heading text-white italic text-center max-w-2xl mx-auto mb-8">
                    "Cuando el cuerpo está protegido, el alma puede permanecer abierta."
                  </blockquote>

                  {/* CTA */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button 
                      className="px-8 py-4 rounded-full font-medium transition-all inline-flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'white', color: palette.primary }}
                    >
                      Solicitar acceso
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button 
                      className="px-8 py-4 rounded-full font-medium transition-all inline-flex items-center justify-center gap-2 text-white"
                      style={{ backgroundColor: palette.accent }}
                    >
                      Ver detalles
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Light section preview */}
              <div className="mt-6 rounded-2xl bg-gray-50 p-8">
                <h4 className="text-lg font-heading mb-4" style={{ color: palette.primary }}>
                  Vista en sección clara
                </h4>
                <div className="flex flex-wrap gap-3">
                  <span 
                    className="px-4 py-2 rounded-full text-sm text-white"
                    style={{ backgroundColor: palette.primary }}
                  >
                    Botón primario
                  </span>
                  <span 
                    className="px-4 py-2 rounded-full text-sm border-2"
                    style={{ borderColor: palette.primary, color: palette.primary }}
                  >
                    Botón outline
                  </span>
                  <span 
                    className="px-4 py-2 rounded-full text-sm text-white"
                    style={{ backgroundColor: palette.accent }}
                  >
                    Botón dorado
                  </span>
                </div>
                <div className="mt-4 p-4 rounded-xl border-2" style={{ borderColor: palette.primary }}>
                  <p className="text-sm" style={{ color: palette.primary }}>
                    <strong>Card destacada</strong> con borde del color primario
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-20 p-8 bg-gray-100 rounded-3xl">
          <h2 className="text-2xl font-heading mb-6 text-center">Mi Recomendación Profesional</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl">
              <div className="w-12 h-12 rounded-full mb-4" style={{ backgroundColor: '#5D4E42' }}></div>
              <h3 className="font-heading text-lg mb-2">🥇 Tierra Cálida</h3>
              <p className="text-sm text-gray-600">
                Conecta con tu metáfora de "arquitectura interior". Cálido, grounding, profesional. 
                Evoca raíces y estabilidad sin ser pesado.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl">
              <div className="w-12 h-12 rounded-full mb-4" style={{ backgroundColor: '#2d4a4a' }}></div>
              <h3 className="font-heading text-lg mb-2">🥈 Teal Profundo</h3>
              <p className="text-sm text-gray-600">
                Equilibrio perfecto entre lo terapéutico y lo espiritual. Color asociado con 
                sanación y fluidez emocional.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl">
              <div className="w-12 h-12 rounded-full mb-4" style={{ backgroundColor: '#1e3a5f' }}></div>
              <h3 className="font-heading text-lg mb-2">🥉 Azul Profundo</h3>
              <p className="text-sm text-gray-600">
                Ya lo usas en Campus. Coherente, sereno, profundo. Transmite confianza y 
                profundidad espiritual.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorTest;
